/**
 * SEO Content Preview Component
 * Displays SEO-optimized content with editable SERP preview and schema
 */

import React, { useState, useEffect } from 'react';
import {
  Search, Globe, Link, Hash, FileText, BarChart,
  Check, X, AlertCircle, Eye, Code, Target, Clock, Edit2, Save, User
} from 'lucide-react';
import { SEOContentMetadata } from '../../types/asset-metadata';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { TipTapEditor } from '../../asset-editors/TipTapEditor';

interface SEOPreviewProps {
  asset: {
    id: number;
    file_path?: string;
    file_name?: string;
    title?: string;
    metadata?: any;
    content?: any;
  };
  apiBaseUrl?: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

export default function SEOPreview({ asset, apiBaseUrl = '', onSave }: SEOPreviewProps) {
  // Parse metadata if it's a string
  const parseMetadata = () => {
    if (typeof asset.metadata === 'string') {
      try {
        return JSON.parse(asset.metadata);
      } catch (e) {
        return {};
      }
    }
    return asset.metadata || {};
  };

  // Also check content field for metadata
  const parseContent = () => {
    if (typeof asset.content === 'string') {
      try {
        return JSON.parse(asset.content);
      } catch (e) {
        return {};
      }
    }
    return asset.content || {};
  };

  // Merge metadata from both sources, content takes precedence
  const metadataFromAsset = parseMetadata();
  const contentData = parseContent();
  const initialMetadata: SEOContentMetadata = { ...metadataFromAsset, ...contentData };

  const [metadata, setMetadata] = useState(initialMetadata);
  const [isEditingSerp, setIsEditingSerp] = useState(false);
  const [isEditingSchema, setIsEditingSchema] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [schemaText, setSchemaText] = useState(JSON.stringify(metadata.structured_data || metadata.schema_markup || {}, null, 2));
  const [pageContent, setPageContent] = useState(metadata.pageContent || '<article><h1>Page Title</h1><p>Page content goes here...</p></article>');

  // Use sessionStorage to persist the active tab for this asset
  const tabStorageKey = `seo-preview-tab-${asset.id}`;
  const savedTab = sessionStorage.getItem(tabStorageKey) as 'serp' | 'content' | 'schema' | 'additional' | null;
  const [activeTab, setActiveTab] = useState<'serp' | 'content' | 'schema' | 'additional'>(savedTab || 'serp');

  const [isEditingAdditional, setIsEditingAdditional] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Save tab selection to sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem(tabStorageKey, activeTab);
  }, [activeTab, tabStorageKey]);

  // Only update content when asset actually changes (not just re-renders)
  const prevAssetIdRef = React.useRef(asset.id);
  React.useEffect(() => {
    // Only reset state if we're switching to a different asset
    if (prevAssetIdRef.current !== asset.id) {
      // Parse metadata from asset
      const parsedMetadata = typeof asset.metadata === 'string' ?
        ((() => { try { return JSON.parse(asset.metadata); } catch { return {}; } })()) :
        (asset.metadata || {});

      const parsedContent = typeof asset.content === 'string' ?
        ((() => { try { return JSON.parse(asset.content); } catch { return {}; } })()) :
        (asset.content || {});

      const newMetadata = { ...parsedMetadata, ...parsedContent };
      setMetadata(newMetadata);
      setPageContent(newMetadata.pageContent || '<article><h1>Page Title</h1><p>Page content goes here...</p></article>');
      setSchemaText(JSON.stringify(newMetadata.structured_data || newMetadata.schema_markup || {}, null, 2));

      // Load saved tab for new asset
      const newSavedTab = sessionStorage.getItem(`seo-preview-tab-${asset.id}`) as 'serp' | 'content' | 'schema' | 'additional' | null;
      setActiveTab(newSavedTab || 'serp');

      prevAssetIdRef.current = asset.id;
    }
  }, [asset.id, asset.metadata, asset.content]);

  // Quill modules configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'],
      ['clean']
    ]
  };

  const handleSave = async (currentTabOnly?: 'serp' | 'schema' | 'content' | 'additional') => {
    if (!onSave) {
      alert('Save function not available');
      return;
    }

    setIsSaving(true);
    try {
      // Parse schema text back to object
      let parsedSchema = {};
      try {
        parsedSchema = JSON.parse(schemaText);
      } catch (e) {
        console.error('Invalid schema JSON:', e);
        parsedSchema = {}; // Use empty object if JSON is invalid
      }

      const updatedMetadata = {
        ...metadata,
        structured_data: parsedSchema,
        schema_markup: parsedSchema,
        pageContent: pageContent
      };

      console.log('Saving SEO data for asset:', asset.id, updatedMetadata);
      await onSave(asset.id, JSON.stringify(updatedMetadata));
      console.log('SEO data saved successfully');
      alert('SEO content saved successfully!');

      // Only reset the editing state for the current tab if specified
      if (currentTabOnly) {
        switch (currentTabOnly) {
          case 'serp':
            setIsEditingSerp(false);
            break;
          case 'schema':
            setIsEditingSchema(false);
            break;
          case 'content':
            setIsEditingContent(false);
            break;
          case 'additional':
            setIsEditingAdditional(false);
            break;
        }
      } else {
        // Reset all if no specific tab (backward compatibility)
        setIsEditingSerp(false);
        setIsEditingSchema(false);
        setIsEditingContent(false);
        setIsEditingAdditional(false);
      }
    } catch (error) {
      console.error('Failed to save SEO content:', error);
      alert('Failed to save SEO content: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate character counts
  const titleLength = metadata.page_title?.length || 0;
  const descLength = metadata.meta_description?.length || 0;
  const titleColor = titleLength > 60 ? 'text-red-600' : titleLength > 50 ? 'text-yellow-600' : 'text-green-600';
  const descColor = descLength > 160 ? 'text-red-600' : descLength > 150 ? 'text-yellow-600' : 'text-green-600';

  // SERP Preview with Editable Fields
  const SERPPreview = () => (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Search Engine Result Preview</h3>
        <button
          onClick={() => {
            if (isSaving) return; // Prevent multiple clicks while saving
            isEditingSerp ? handleSave('serp') : setIsEditingSerp(true);
          }}
          disabled={isSaving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {isEditingSerp ? (
            <>
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </>
          ) : (
            <>
              <Edit2 className="w-4 h-4" />
              <span>Edit SERP</span>
            </>
          )}
        </button>
      </div>

      {/* Google SERP Preview */}
      <div className="bg-white rounded-lg shadow-md border p-6 mb-6">
        <h4 className="text-sm font-semibold text-gray-600 mb-4">Google Search Result</h4>

        {/* URL */}
        <div className="mb-3">
          {isEditingSerp ? (
            <input
              type="text"
              value={metadata.canonical_url || ''}
              onChange={(e) => setMetadata({ ...metadata, canonical_url: e.target.value })}
              className="w-full text-green-700 text-sm border rounded px-2 py-1"
              placeholder="https://example.com/page"
            />
          ) : (
            <span className="text-green-700 text-sm">
              {metadata.canonical_url || 'https://example.com/page'}
            </span>
          )}
        </div>

        {/* Title */}
        <div className="mb-3">
          {isEditingSerp ? (
            <div>
              <input
                type="text"
                value={metadata.page_title || ''}
                onChange={(e) => setMetadata({ ...metadata, page_title: e.target.value })}
                className="w-full text-xl text-blue-700 border rounded px-2 py-1 font-medium"
                placeholder="Page Title"
                maxLength={70}
              />
              <span className={`text-xs ${titleColor} mt-1`}>
                {titleLength}/60 characters (recommended)
              </span>
            </div>
          ) : (
            <h2 className="text-xl text-blue-700 hover:underline cursor-pointer">
              {metadata.page_title || 'Page Title'}
            </h2>
          )}
        </div>

        {/* Description */}
        <div>
          {isEditingSerp ? (
            <div>
              <textarea
                value={metadata.meta_description || ''}
                onChange={(e) => setMetadata({ ...metadata, meta_description: e.target.value })}
                className="w-full text-sm text-gray-600 border rounded px-2 py-1 h-20"
                placeholder="Meta description goes here..."
                maxLength={180}
              />
              <span className={`text-xs ${descColor} mt-1`}>
                {descLength}/160 characters (recommended)
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-600 leading-relaxed">
              {metadata.meta_description || 'Meta description goes here...'}
            </p>
          )}
        </div>
      </div>

      {/* Social Media Preview */}
      <div className="bg-white rounded-lg shadow-md border overflow-hidden">
        <h4 className="text-sm font-semibold text-gray-600 px-6 pt-6 pb-3">Social Media Preview (Facebook/Twitter)</h4>

        {/* OG Image */}
        {metadata.og_image && (
          <div className="relative">
            <img
              src={metadata.og_image}
              alt="OG Image"
              className="w-full h-52 object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            {isEditingSerp && (
              <input
                type="text"
                value={metadata.og_image || ''}
                onChange={(e) => setMetadata({ ...metadata, og_image: e.target.value })}
                className="absolute bottom-2 left-2 right-2 text-xs border rounded px-2 py-1 bg-white/90"
                placeholder="Image URL"
              />
            )}
          </div>
        )}

        <div className="p-6">
          {/* OG Title */}
          {isEditingSerp ? (
            <input
              type="text"
              value={metadata.og_title || metadata.page_title || ''}
              onChange={(e) => setMetadata({ ...metadata, og_title: e.target.value })}
              className="w-full font-semibold border rounded px-2 py-1 mb-2"
              placeholder="Social Media Title"
            />
          ) : (
            <h3 className="font-semibold mb-2">
              {metadata.og_title || metadata.page_title || 'Social Media Title'}
            </h3>
          )}

          {/* OG Description */}
          {isEditingSerp ? (
            <textarea
              value={metadata.og_description || metadata.meta_description || ''}
              onChange={(e) => setMetadata({ ...metadata, og_description: e.target.value })}
              className="w-full text-sm text-gray-600 border rounded px-2 py-1 h-16"
              placeholder="Social media description..."
            />
          ) : (
            <p className="text-sm text-gray-600 mb-2">
              {metadata.og_description || metadata.meta_description || 'Social media description...'}
            </p>
          )}

          <span className="text-xs text-gray-500">
            {metadata.canonical_url ? new URL(metadata.canonical_url).hostname : 'example.com'}
          </span>
        </div>
      </div>

    </div>
  );

  // Additional SEO Fields Component
  const AdditionalSEOFields = () => (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Additional SEO Fields</h3>
        <button
          onClick={() => {
            if (isSaving) return;
            isEditingAdditional ? handleSave('additional') : setIsEditingAdditional(true);
          }}
          disabled={isSaving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {isEditingAdditional ? (
            <>
              <Save className="w-4 h-4" />
              <span>Save Fields</span>
            </>
          ) : (
            <>
              <Edit2 className="w-4 h-4" />
              <span>Edit Fields</span>
            </>
          )}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Keywords */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Hash className="inline w-4 h-4 mr-1" />
              Keywords
            </label>
            <input
              type="text"
              value={Array.isArray(metadata.keywords) ? metadata.keywords.join(', ') : ''}
              onChange={(e) => setMetadata({
                ...metadata,
                keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
              })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="keyword1, keyword2, keyword3"
            />
            <p className="mt-1 text-xs text-gray-500">Comma-separated list of target keywords</p>
          </div>

          {/* Author */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline w-4 h-4 mr-1" />
              Author
            </label>
            <input
              type="text"
              value={metadata.author || ''}
              onChange={(e) => setMetadata({ ...metadata, author: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="John Doe"
            />
          </div>

          {/* Robots Meta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Target className="inline w-4 h-4 mr-1" />
              Robots Meta
            </label>
            <select
              value={metadata.robots || 'index, follow'}
              onChange={(e) => setMetadata({ ...metadata, robots: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="index, follow">Index, Follow</option>
              <option value="noindex, follow">No Index, Follow</option>
              <option value="index, nofollow">Index, No Follow</option>
              <option value="noindex, nofollow">No Index, No Follow</option>
              <option value="">None (default)</option>
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="inline w-4 h-4 mr-1" />
              Language
            </label>
            <input
              type="text"
              value={metadata.language || ''}
              onChange={(e) => setMetadata({ ...metadata, language: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="en-US"
            />
          </div>

          {/* Viewport */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Eye className="inline w-4 h-4 mr-1" />
              Viewport
            </label>
            <input
              type="text"
              value={metadata.viewport || ''}
              onChange={(e) => setMetadata({ ...metadata, viewport: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="width=device-width, initial-scale=1"
            />
          </div>

          {/* Twitter Card Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Twitter Card Type
            </label>
            <select
              value={metadata.twitter_card || 'summary_large_image'}
              onChange={(e) => setMetadata({ ...metadata, twitter_card: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="summary">Summary</option>
              <option value="summary_large_image">Summary Large Image</option>
              <option value="app">App</option>
              <option value="player">Player</option>
            </select>
          </div>

          {/* Twitter Handle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Twitter Handle
            </label>
            <input
              type="text"
              value={metadata.twitter_handle || ''}
              onChange={(e) => setMetadata({ ...metadata, twitter_handle: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="@yourhandle"
            />
          </div>

          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Type
            </label>
            <input
              type="text"
              value={metadata.content_type || ''}
              onChange={(e) => setMetadata({ ...metadata, content_type: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="text/html; charset=UTF-8"
            />
          </div>

          {/* Refresh */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline w-4 h-4 mr-1" />
              Refresh (seconds)
            </label>
            <input
              type="number"
              value={metadata.refresh || ''}
              onChange={(e) => setMetadata({ ...metadata, refresh: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder="0 (no refresh)"
            />
          </div>

          {/* Priority Pages */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority Pages (Internal Links)
            </label>
            <textarea
              value={metadata.priority_pages || ''}
              onChange={(e) => setMetadata({ ...metadata, priority_pages: e.target.value })}
              disabled={!isEditingAdditional}
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 h-20"
              placeholder="/products\n/services\n/about"
            />
            <p className="mt-1 text-xs text-gray-500">One URL per line for internal linking priority</p>
          </div>
        </div>
      </div>

      {/* SEO Score Summary */}
      <div className="mt-6 bg-white rounded-lg shadow-md border p-6">
        <h4 className="text-sm font-semibold text-gray-600 mb-4">SEO Completeness</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm">Meta Title</span>
            <span className={metadata.page_title ? 'text-green-600' : 'text-red-600'}>
              {metadata.page_title ? '✓' : '✗'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Meta Description</span>
            <span className={metadata.meta_description ? 'text-green-600' : 'text-red-600'}>
              {metadata.meta_description ? '✓' : '✗'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Keywords</span>
            <span className={metadata.keywords?.length > 0 ? 'text-green-600' : 'text-yellow-600'}>
              {metadata.keywords?.length > 0 ? '✓' : '○'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Open Graph Tags</span>
            <span className={metadata.og_title || metadata.og_description ? 'text-green-600' : 'text-yellow-600'}>
              {metadata.og_title || metadata.og_description ? '✓' : '○'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Schema Markup</span>
            <span className={metadata.structured_data || metadata.schema_markup ? 'text-green-600' : 'text-yellow-600'}>
              {metadata.structured_data || metadata.schema_markup ? '✓' : '○'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Schema Markup Editor
  const SchemaEditor = () => (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Schema Markup / Structured Data</h3>
        <button
          onClick={() => {
            if (isSaving) return;
            isEditingSchema ? handleSave('schema') : setIsEditingSchema(true);
          }}
          disabled={isSaving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {isEditingSchema ? (
            <>
              <Save className="w-4 h-4" />
              <span>Save Schema</span>
            </>
          ) : (
            <>
              <Edit2 className="w-4 h-4" />
              <span>Edit Schema</span>
            </>
          )}
        </button>
      </div>

      <div className="bg-gray-900 text-gray-100 rounded-lg overflow-hidden">
        {isEditingSchema ? (
          <textarea
            value={schemaText}
            onChange={(e) => setSchemaText(e.target.value)}
            className="w-full h-96 p-6 bg-gray-900 text-gray-100 font-mono text-sm focus:outline-none"
            placeholder='{"@context": "https://schema.org", "@type": "..."}'
            spellCheck={false}
          />
        ) : (
          <pre className="p-6 overflow-x-auto">
            <code className="text-sm">{schemaText}</code>
          </pre>
        )}
      </div>

      {isEditingSchema && (
        <div className="mt-2 text-sm text-gray-600">
          <p>Enter valid JSON for schema.org structured data.</p>
        </div>
      )}

      {/* Schema Validation Status */}
      <div className="mt-4 flex items-center space-x-2">
        {(() => {
          try {
            JSON.parse(schemaText);
            return (
              <>
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-600">Valid JSON</span>
              </>
            );
          } catch {
            return (
              <>
                <X className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-600">Invalid JSON</span>
              </>
            );
          }
        })()}
      </div>
    </div>
  );

  // Page Content Editor Component
  const PageContentEditor = () => (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Page Content</h3>
        <button
          onClick={() => {
            if (isSaving) return;
            isEditingContent ? handleSave('content') : setIsEditingContent(true);
          }}
          disabled={isSaving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {isEditingContent ? (
            <>
              <Save className="w-4 h-4" />
              <span>Save Content</span>
            </>
          ) : (
            <>
              <Edit2 className="w-4 h-4" />
              <span>Edit Content</span>
            </>
          )}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md border">
        {isEditingContent ? (
          <TipTapEditor
            content={pageContent}
            onChange={(html) => setPageContent(html)}
            assetType="page_content"
            placeholder="Start writing your page content..."
            readOnly={false}
          />
        ) : (
          <div className="p-6">
            <div className="prose prose-lg max-w-none">
              <div dangerouslySetInnerHTML={{ __html: pageContent }} />
            </div>
          </div>
        )}
      </div>

      {/* Content Stats */}
      <div className="mt-4 bg-white rounded-lg shadow-md border p-4">
        <h4 className="text-sm font-semibold text-gray-600 mb-3">Content Statistics</h4>
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Word Count:</span>
            <p className="font-medium">{pageContent.split(/\s+/).filter(word => word.length > 0).length}</p>
          </div>
          <div>
            <span className="text-gray-500">Character Count:</span>
            <p className="font-medium">{pageContent.replace(/<[^>]*>/g, '').length}</p>
          </div>
          <div>
            <span className="text-gray-500">Reading Time:</span>
            <p className="font-medium">{Math.ceil(pageContent.split(/\s+/).filter(word => word.length > 0).length / 200)} min</p>
          </div>
          <div>
            <span className="text-gray-500">HTML Valid:</span>
            <p className="font-medium text-green-600">✓</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Search className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">SEO Content Editor</h2>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('serp')}
              className={`px-4 py-2 rounded ${activeTab === 'serp' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <Globe className="w-4 h-4 inline mr-1" />
              SERP Preview
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`px-4 py-2 rounded ${activeTab === 'content' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <FileText className="w-4 h-4 inline mr-1" />
              Page Content
            </button>
            <button
              onClick={() => setActiveTab('additional')}
              className={`px-4 py-2 rounded ${activeTab === 'additional' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <Target className="w-4 h-4 inline mr-1" />
              Additional SEO
            </button>
            <button
              onClick={() => setActiveTab('schema')}
              className={`px-4 py-2 rounded ${activeTab === 'schema' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            >
              <Code className="w-4 h-4 inline mr-1" />
              Schema Markup
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'serp' && <SERPPreview />}
        {activeTab === 'content' && <PageContentEditor />}
        {activeTab === 'additional' && <AdditionalSEOFields />}
        {activeTab === 'schema' && <SchemaEditor />}
      </div>

      {/* Footer Status Bar */}
      <div className="bg-white border-t px-4 py-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span className={`flex items-center space-x-1 ${titleColor}`}>
              <FileText className="w-4 h-4" />
              <span>Title: {titleLength} chars</span>
            </span>
            <span className={`flex items-center space-x-1 ${descColor}`}>
              <Hash className="w-4 h-4" />
              <span>Description: {descLength} chars</span>
            </span>
          </div>
          {isSaving && (
            <span className="text-blue-600">Saving...</span>
          )}
        </div>
      </div>
    </div>
  );
}